import { newId } from './ulid'
import { clone, deepEqual } from './util'
import type { Data } from './types'

export type WhereOp = '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'array-contains'
const WHERE_OPS: WhereOp[] = ['==', '!=', '<', '<=', '>', '>=', 'in', 'array-contains']

export type Constraint =
  | { kind: 'where'; field: string; op: WhereOp; value: unknown }
  | { kind: 'orderBy'; field: string; dir: 'asc' | 'desc' }
  | { kind: 'limit'; n: number }

export class CollectionReference {
  readonly type = 'collection' as const
  constructor(readonly path: string) {}
  get id(): string {
    return this.path.slice(this.path.lastIndexOf('/') + 1)
  }
  get parent(): DocumentReference | null {
    const i = this.path.lastIndexOf('/')
    return i < 0 ? null : new DocumentReference(this.path.slice(0, i))
  }
}

export class DocumentReference {
  readonly type = 'document' as const
  constructor(readonly path: string) {}
  get id(): string {
    return this.path.slice(this.path.lastIndexOf('/') + 1)
  }
  get parent(): CollectionReference {
    return new CollectionReference(this.path.slice(0, this.path.lastIndexOf('/')))
  }
}

export class Query {
  readonly type = 'query' as const
  constructor(
    readonly path: string,
    readonly constraints: readonly Constraint[],
  ) {}
}

export type QueryTarget = CollectionReference | Query

function segments(parts: unknown[]): string[] {
  const out: string[] = []
  for (const p of parts) {
    if (typeof p !== 'string' && typeof p !== 'number') throw new Error(`Invalid path segment: ${String(p)}`)
    for (const s of String(p).split('/')) {
      if (!s) throw new Error(`Invalid path: empty segment in "${String(p)}"`)
      out.push(s)
    }
  }
  return out
}

function basePath(first: unknown): string | null {
  if (first instanceof CollectionReference || first instanceof DocumentReference) return first.path
  return null
}

// First argument is `db` (ignored, may be anything) or a reference to extend.
export function collection(first?: unknown, ...parts: unknown[]): CollectionReference {
  const base = basePath(first)
  if (first instanceof CollectionReference && parts.length === 0) return first
  const segs = [...(base ? base.split('/') : []), ...segments(parts)]
  if (segs.length % 2 !== 1) throw new Error(`Invalid collection path "${segs.join('/')}" (needs an odd number of segments)`)
  return new CollectionReference(segs.join('/'))
}

export function doc(first?: unknown, ...parts: unknown[]): DocumentReference {
  const base = basePath(first)
  if (first instanceof DocumentReference && parts.length === 0) return first
  const rest = segments(parts)
  if (first instanceof CollectionReference && rest.length === 0) rest.push(newId())
  const segs = [...(base ? base.split('/') : []), ...rest]
  if (segs.length === 0 || segs.length % 2 !== 0) {
    throw new Error(`Invalid document path "${segs.join('/')}" (needs an even number of segments)`)
  }
  return new DocumentReference(segs.join('/'))
}

export function where(field: string, op: WhereOp, value: unknown): Constraint {
  if (!WHERE_OPS.includes(op)) throw new Error(`Unsupported where operator "${String(op)}"`)
  if (op === 'in' && !Array.isArray(value)) throw new Error('"in" needs an array')
  if (field.includes('.')) throw new Error('Dotted field paths are not supported')
  return { kind: 'where', field, op, value }
}

export function orderBy(field: string, dir: 'asc' | 'desc' = 'asc'): Constraint {
  if (field.includes('.')) throw new Error('Dotted field paths are not supported')
  return { kind: 'orderBy', field, dir }
}

export function limit(n: number): Constraint {
  if (!Number.isInteger(n) || n <= 0) throw new Error('limit must be a positive integer')
  return { kind: 'limit', n }
}

export function query(target: QueryTarget, ...constraints: Constraint[]): Query {
  if (target instanceof Query) return new Query(target.path, [...target.constraints, ...constraints])
  if (target instanceof CollectionReference) return new Query(target.path, constraints)
  throw new Error('query() needs a collection reference or a query')
}

function rank(v: unknown): number {
  if (v === undefined) return 0
  if (v === null) return 1
  if (typeof v === 'boolean') return 2
  if (typeof v === 'number') return 3
  if (typeof v === 'string') return 4
  return 5
}

function compare(a: unknown, b: unknown): number {
  const ra = rank(a)
  const rb = rank(b)
  if (ra !== rb) return ra - rb
  if (ra === 2 || ra === 3) return Number(a) - Number(b)
  if (ra === 4) return (a as string) < (b as string) ? -1 : (a as string) > (b as string) ? 1 : 0
  return 0
}

function matches(data: Data, c: Extract<Constraint, { kind: 'where' }>): boolean {
  const v = data[c.field]
  switch (c.op) {
    case '==':
      return v !== undefined && deepEqual(v, c.value)
    case '!=':
      return v !== undefined && !deepEqual(v, c.value)
    case 'in':
      return v !== undefined && (c.value as unknown[]).some((x) => deepEqual(v, x))
    case 'array-contains':
      return Array.isArray(v) && v.some((x) => deepEqual(x, c.value))
    default: {
      // Range filters only match values of the same type, as Firestore does.
      const r = rank(v)
      if (r < 2 || r > 4 || r !== rank(c.value)) return false
      const n = compare(v, c.value)
      return c.op === '<' ? n < 0 : c.op === '<=' ? n <= 0 : c.op === '>' ? n > 0 : n >= 0
    }
  }
}

export interface Entry {
  path: string
  id: string
  data: Data
}

export function runQuery(entries: Entry[], constraints: readonly Constraint[]): Entry[] {
  const wheres = constraints.filter((c): c is Extract<Constraint, { kind: 'where' }> => c.kind === 'where')
  const orders = constraints.filter((c): c is Extract<Constraint, { kind: 'orderBy' }> => c.kind === 'orderBy')
  let n = Infinity
  for (const c of constraints) if (c.kind === 'limit') n = c.n

  const out = entries.filter((e) => wheres.every((w) => matches(e.data, w)))
  out.sort((a, b) => {
    for (const o of orders) {
      const d = compare(a.data[o.field], b.data[o.field])
      if (d !== 0) return o.dir === 'desc' ? -d : d
    }
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
  return n < out.length ? out.slice(0, n) : out
}

export class DocumentSnapshot {
  constructor(
    readonly ref: DocumentReference,
    private readonly _data: Data | null,
  ) {}
  get id(): string {
    return this.ref.id
  }
  exists(): boolean {
    return this._data !== null
  }
  data(): Data | undefined {
    return this._data === null ? undefined : clone(this._data)
  }
  get(field: string): unknown {
    return this._data === null ? undefined : clone(this._data[field])
  }
}

export class QueryDocumentSnapshot extends DocumentSnapshot {
  override data(): Data {
    return super.data() as Data
  }
}

export class QuerySnapshot {
  readonly docs: QueryDocumentSnapshot[]
  constructor(entries: Entry[]) {
    this.docs = entries.map((e) => new QueryDocumentSnapshot(new DocumentReference(e.path), e.data))
  }
  get size(): number {
    return this.docs.length
  }
  get empty(): boolean {
    return this.docs.length === 0
  }
  forEach(cb: (d: QueryDocumentSnapshot) => void): void {
    this.docs.forEach((d) => cb(d))
  }
}
